import moment from 'moment';
import {
  ScheduleMaintenance,
  TimeMaintenance,
  TimeMaintenanceAdhoc,
} from 'src/models';
import { dayJs } from 'src/utilities/day';
import { getTranslation } from 'src/utilities/translator';
import { FAILED_TRANSACTION_REASON } from './constants';

export const getFailedTransactionMessage = (status: string) => {
  switch (status) {
    case FAILED_TRANSACTION_REASON.ACCOUNT_NOT_FOUND:
      return getTranslation('dashboard_errorMapping_accountNotFound');
    case FAILED_TRANSACTION_REASON.ACCOUNT_INACTIVE:
      return getTranslation('dashboard_errorMapping_accountInactive');
    case FAILED_TRANSACTION_REASON.ACCOUNT_BLOCKED:
      return getTranslation('dashboard_errorMapping_accountBlocked');
    case FAILED_TRANSACTION_REASON.ACCOUNT_EXCEED_LIMIT:
      return getTranslation('dashboard_errorMapping_accountExceedLimit');
    case FAILED_TRANSACTION_REASON.INVALID_SIGNATURE:
      return getTranslation('dashboard_errorMapping_invalidSignature');
    case FAILED_TRANSACTION_REASON.CLIENT_INSUFFICIENT_BALANCE:
      return getTranslation('dashboard_errorMapping_insufficientBalance');
    case FAILED_TRANSACTION_REASON.VENDOR_UNDER_MAINTENANCE:
      return getTranslation('dashboard_errorMapping_vendorUnderMaintenance');
    case FAILED_TRANSACTION_REASON.VENDOR_SYSTEM_ERROR:
    case FAILED_TRANSACTION_REASON.TIMEOUT:
      return getTranslation('dashboard_errorMapping_vendorError');
    case FAILED_TRANSACTION_REASON.INTERNAL_SYSTEM_ERROR:
    case FAILED_TRANSACTION_REASON.DUPLICATE_INTERNAL_TX_ID:
    case FAILED_TRANSACTION_REASON.OTHERS:
      return getTranslation('dashboard_errorMapping_systemErrorContact');
    case FAILED_TRANSACTION_REASON.AMOUNT_EXCEED_LIMIT:
      return getTranslation('dashboard_errorMapping_amountExceedLimit');
    case FAILED_TRANSACTION_REASON.SUSPECTED_FRAUD:
    case FAILED_TRANSACTION_REASON.USER_ROUTING_PREFERENCES:
      return getTranslation('dashboard_errorMapping_cannotBeProcessed');
    case FAILED_TRANSACTION_REASON.INVALID_RECIPIENT_ACCOUNT_NUMBER:
      return getTranslation(
        'dashboard_errorMapping_invalidRecipientAccountNumber',
      );
    case FAILED_TRANSACTION_REASON.UNAUTHORIZED_KEY:
    case FAILED_TRANSACTION_REASON.INVALID_REQUEST_AMOUNT:
    case FAILED_TRANSACTION_REASON.INVALID_SENDER_INFO:
    case FAILED_TRANSACTION_REASON.INVALID_RECIPIENT_INFO:
    case FAILED_TRANSACTION_REASON.INVALID_REQUEST_ADDITIONAL_DATA:
    case FAILED_TRANSACTION_REASON.VENDOR_IS_NOT_SUPPORTED:
    case FAILED_TRANSACTION_REASON.INVALID_DATA:
    case FAILED_TRANSACTION_REASON.INVALID_ACCOUNT_TYPE:
    case FAILED_TRANSACTION_REASON.EXCEPTION:
    case FAILED_TRANSACTION_REASON.INSUFFICIENT_BALANCE:
    default:
      return getTranslation('dashboard_errorMapping_systemError');
  }
};

export const formatTimeStringToUTC = (date: string, format?: string) => {
  return moment
    .utc(date)
    .local()
    .format(format ?? 'DD/MM/YYYY, HH:mm');
};

export const isFilterDefaultDate = (dateFrom: Date, dateTo: Date) => {
  const minusDateKey = [0, 7, 30, 60, 89]; // diff date from default date
  if (dayJs(dateTo).isSame(dayJs(), 'day')) {
    const daysDifferences = dayJs(dateTo).diff(dateFrom, 'days');
    return minusDateKey.includes(daysDifferences);
  }
  return false;
};

export const checkUnderMaintenanceUsingTimesAdhoc = (
  times: TimeMaintenanceAdhoc[],
) => {
  if (times.length === 0) return false;
  const now = dayJs();
  return times.some(time => {
    return (
      now.isAfter(time.date_time_start) && now.isBefore(time.date_time_end)
    );
  });
};

const checkTimeIsBetweenStartAndEnd = (
  start: string,
  end: string,
  now: string,
) => {
  if (start <= end) {
    return now >= start && now <= end;
  }
  return now >= start || now <= end;
};

const checkUnderMaintenanceUsingTimes = (times: TimeMaintenance[]) => {
  if (times.length === 0) return false;
  const hourNow = dayJs().format('HH:mm');
  return times.some(time => {
    return checkTimeIsBetweenStartAndEnd(time.start, time.end, hourNow);
  });
};

export const checkUnderMaintenanceUsingSchedule = (
  schedules: ScheduleMaintenance[],
) => {
  if (schedules.length === 0) return false;
  const now = dayJs();
  const day = now.day();
  return schedules.some(schedule => {
    return (
      schedule.days_of_week.includes(day) &&
      checkUnderMaintenanceUsingTimes(schedule.times)
    );
  });
};

export const generateDescriptionMaintenance = (daysOfWeek: number[]) => {
  if (daysOfWeek.length === 7) {
    return getTranslation('maintenanceSchedule_message_everyday');
  }
  if (daysOfWeek.length === 1) {
    return `${getTranslation(
      `maintenanceSchedule_message_${moment()
        .day(daysOfWeek[0])
        .format('dddd')
        .toLowerCase()}`,
    )}`;
  }
  if (daysOfWeek.every(day => [1, 2, 3, 4, 5].includes(day))) {
    return getTranslation('maintenanceSchedule_message_mondayToFriday');
  }
  const days = daysOfWeek.map(day =>
    getTranslation(`common_${moment().day(day).format('dddd').toLowerCase()}`),
  );

  if (days.length === 2) {
    return `${getTranslation('maintenanceSchedule_message_on')} ${days.join(
      ` ${getTranslation('common_and')} `,
    )}`;
  }
  return `${getTranslation('maintenanceSchedule_message_on')} ${days
    .slice(0, -1)
    .join(`, `)}, ${getTranslation('common_and')} ${days.slice(-1)}`;
};

export const generateDateAdhoc = (date: string) => {
  return `${dayJs(date).format('dddd, DD MMMM YYYY')} ${getTranslation(
    'common_atTime',
  )} ${dayJs(date).format('HH:mm')} WIB`;
};

// Add firebase to max hour
export const isLessThan24Hours = (date: string | null | undefined) =>
  dayJs(date).diff(dayJs(), 'minutes') < 60 * 24;
